import { supabase } from '../supabase';
import { MarketComment, MarketCommentAttachment } from '../utils/types';

export interface CreateMarketCommentInput {
  exporter_id: number;
  created_by: number;
  content?: string | null;
  date_from: string; // YYYY-MM-DD
  date_to?: string | null; // YYYY-MM-DD
  species_id?: number | null;
  season?: string | null;
  market_id?: string | null;
  variety?: string | null;
  caliber?: string | null;
  seller?: string | null;
  exporter_ref?: string | null;
  container_number?: string | null;
  label?: string | null;
  format?: string | null;
  color?: string | null;
  category?: string | null;
  unit_price?: number | null;
  unit_weight?: number | null;
  local_currency?: string | null;
  unit_count?: number | null;
}

export interface ListMarketCommentsParams {
  // Either a single date or a range
  date?: string; // YYYY-MM-DD
  from?: string; // YYYY-MM-DD
  to?: string; // YYYY-MM-DD

  species_id?: number;
  season?: string;
  market_id?: string;
  variety?: string;
  caliber?: string;
  seller?: string;
  exporter_ref?: string;
  container_number?: string;
  label?: string;

  limit?: number;
  offset?: number;
}

export async function createMarketComment(
  payload: CreateMarketCommentInput
): Promise<MarketComment> {
  const { data, error } = await supabase
    .from('market_comments')
    .insert(payload)
    .select(`
      *,
      created_by:users!market_comments_created_by_fkey(
        id, first_name, last_name
      )
    `)
    .single();

  if (error) throw error;
  return data as unknown as MarketComment;
}

export async function updateMarketComment(
  id: number,
  patch: Partial<Omit<CreateMarketCommentInput, 'exporter_id' | 'date_from'>> & {
    content?: string;
    date_from?: string;
    date_to?: string | null;
  }
): Promise<MarketComment> {
  const { data, error } = await supabase
    .from('market_comments')
    .update(patch)
    .eq('id', id)
    .select(`
      *,
      created_by:users!market_comments_created_by_fkey(
        id, first_name, last_name
      )
    `)
    .single();

  if (error) throw error;
  return data as unknown as MarketComment;
}

export async function deleteMarketComment(id: number): Promise<boolean> {
  const { error } = await supabase
    .from('market_comments')
    .update({ deleted_at: new Date().toISOString() })
    .eq('id', id);

  if (error) throw error;
  return true;
}

export async function listMarketComments(
  params: ListMarketCommentsParams
): Promise<MarketComment[]> {
  let query = supabase
    .from('market_comments')
    .select(`
      *,
      created_by:users!market_comments_created_by_fkey(
        id, first_name, last_name
      ),
      attachments:market_comment_attachments(*)
    `)
    .is('deleted_at', null)
    .order('created_at', { ascending: false })
    .order('date_from', { ascending: false });

  const {
    date,
    from,
    to,
    species_id,
    season,
    market_id,
    variety,
    caliber,
    seller,
    exporter_ref,
    container_number,
    label,
    limit,
    offset,
  } = params;

  // Date logic
  if (date) {
    // Overlaps the single date. Handle open-ended date_to NULL too.
    // (date_from <= date) AND (date_to >= date OR (date_to IS NULL AND date_from = date))
    query = query
      .lte('date_from', date)
      .or(`date_to.gte.${date},and(date_to.is.null,date_from.eq.${date})`);
  } else if (from && to) {
    // Overlap range: (date_from <= to) AND (coalesce(date_to, date_from) >= from)
    query = query.lte('date_from', to).or(`date_to.gte.${from},and(date_to.is.null,date_from.gte.${from})`);
  }

  if (species_id !== undefined) query = query.eq('species_id', species_id);
  if (season) query = query.eq('season', season);
  if (market_id) query = query.eq('market_id', market_id);
  if (variety) query = query.ilike('variety', variety);
  if (caliber) query = query.ilike('caliber', caliber);
  if (seller) query = query.ilike('seller', seller);
  if (exporter_ref) query = query.ilike('exporter_ref', exporter_ref);
  if (container_number) query = query.ilike('container_number', container_number);
  if (label) query = query.ilike('label', label);

  if (limit) query = query.limit(limit);
  if (offset) query = query.range(offset, (offset || 0) + (limit || 50) - 1);

  const { data, error } = await query;
  if (error) throw error;
  return (data as unknown as MarketComment[]) || [];
}

export async function countMarketComments(
  params: ListMarketCommentsParams
): Promise<number> {
  let query = supabase
    .from('market_comments')
    .select('id', { count: 'exact', head: true })
    .is('deleted_at', null);

  const {
    date,
    from,
    to,
    species_id,
    season,
    market_id,
    variety,
    caliber,
    seller,
    exporter_ref,
    container_number,
    label,
  } = params;

  if (date) {
    query = query
      .lte('date_from', date)
      .or(`date_to.gte.${date},and(date_to.is.null,date_from.eq.${date})`);
  } else if (from && to) {
    query = query
      .lte('date_from', to)
      .or(`date_to.gte.${from},and(date_to.is.null,date_from.gte.${from})`);
  }

  if (species_id !== undefined) query = query.eq('species_id', species_id);
  if (season) query = query.eq('season', season);
  if (market_id) query = query.eq('market_id', market_id);
  if (variety) query = query.ilike('variety', variety);
  if (caliber) query = query.ilike('caliber', caliber);
  if (seller) query = query.ilike('seller', seller);
  if (exporter_ref) query = query.ilike('exporter_ref', exporter_ref);
  if (container_number) query = query.ilike('container_number', container_number);
  if (label) query = query.ilike('label', label);

  const { count, error } = await query;
  if (error) throw error;
  return count || 0;
}

// ---------------------------------------------------------------------------
// Attachments API
// ---------------------------------------------------------------------------

export interface UploadMarketCommentAttachmentOptions {
  commentId: number;
  exporterId: number; // for namespacing the storage path
  created_by: number;
  file: File | Blob & { name?: string; type?: string; size?: number };
  bucket?: string; // default 'market-comments'
  comment?: string; // optional note to store with attachment
  requestKey?: string; // stable key for retrying a local shipment-note upload
}

// ---------------------------------------------------------------------------
// Terms (distinct values) API
// ---------------------------------------------------------------------------

export interface ListMarketCommentTermsParams {
  exporter_id: number;
  species_id?: number;
  season?: string;
  limit?: number; // how many rows to scan for suggestions
}

export interface MarketCommentTerms {
  varieties: string[];
  calibers: string[];
  sellers: string[];
  labels: string[];
  exporter_refs: string[];
  container_numbers: string[];
}

export async function listMarketCommentTerms(
  params: ListMarketCommentTermsParams
): Promise<MarketCommentTerms> {
  const { exporter_id, species_id, season, limit = 1000 } = params;

  let q = supabase
    .from('market_comments')
    .select(
      'variety, caliber, seller, label, exporter_ref, container_number'
    )
    .eq('exporter_id', exporter_id)
    .is('deleted_at', null)
    .order('created_at', { ascending: false })
    .limit(limit);

  if (species_id !== undefined) q = q.eq('species_id', species_id);
  if (season) q = q.eq('season', season);

  const { data, error } = await q;
  if (error) throw error;

  const uniq = (arr: (string | null)[]) => {
    const set = new Set<string>();
    for (const v of arr) {
      const s = (v || '').trim();
      if (!s) continue;
      set.add(s);
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  };

  const varieties = uniq((data || []).map((r: any) => r.variety));
  const calibers = uniq((data || []).map((r: any) => r.caliber));
  const sellers = uniq((data || []).map((r: any) => r.seller));
  const labels = uniq((data || []).map((r: any) => r.label));
  const exporter_refs = uniq((data || []).map((r: any) => r.exporter_ref));
  const container_numbers = uniq((data || []).map((r: any) => r.container_number));

  return {
    varieties,
    calibers,
    sellers,
    labels,
    exporter_refs,
    container_numbers,
  };
}

export async function uploadMarketCommentAttachment(
  opts: UploadMarketCommentAttachmentOptions
): Promise<MarketCommentAttachment> {
  const { commentId, exporterId, created_by } = opts;
  const bucket = opts.bucket || 'market-comments';
  const file = opts.file;
  const fileName = (file as any).name || 'attachment';
  const safeName = fileName.replace(/[^a-zA-Z0-9._-]+/g, '-');
  const id =
    opts.requestKey ||
    (globalThis as any).crypto?.randomUUID?.() ||
    `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const path = `${exporterId}/${commentId}/${id}-${safeName}`;
  if (opts.requestKey) {
    const { data: existing, error: readError } = await supabase
      .from('market_comment_attachments')
      .select('*')
      .eq('comment_id', commentId)
      .eq('bucket', bucket)
      .eq('path', path)
      .is('deleted_at', null)
      .maybeSingle();
    if (readError) throw readError;
    if (existing) return existing as unknown as MarketCommentAttachment;
  }
  const checksum =
    globalThis.crypto?.subtle && typeof file.arrayBuffer === 'function'
      ? Array.from(
          new Uint8Array(
            await globalThis.crypto.subtle.digest(
              'SHA-256',
              await file.arrayBuffer()
            )
          )
        )
          .map((v) => v.toString(16).padStart(2, '0'))
          .join('')
      : null;

  const { error: uploadErr } = await supabase.storage
    .from(bucket)
    .upload(path, file, {
      upsert: false,
      contentType: (file as any).type || undefined,
    });
  if (
    uploadErr &&
    !(
      opts.requestKey &&
      String((uploadErr as { statusCode?: string }).statusCode) === '409'
    )
  )
    throw uploadErr;

  const { data, error } = await supabase
    .from('market_comment_attachments')
    .insert({
      comment_id: commentId,
      created_by,
      bucket,
      path,
      name: safeName,
      content_type: (file as any).type || null,
      size: (file as any).size ?? null,
      checksum,
      is_image: ((file as any).type || '').startsWith('image/'),
      comment: opts.comment || null,
    })
    .select('*')
    .single();

  if (error) {
    // Two responses for the same retry may race after the storage upload.
    // The unique object identity lets both resolve to the one attachment.
    if (opts.requestKey && error.code === '23505') {
      const { data: existing, error: readError } = await supabase
        .from('market_comment_attachments')
        .select('*')
        .eq('comment_id', commentId)
        .eq('bucket', bucket)
        .eq('path', path)
        .is('deleted_at', null)
        .maybeSingle();
      if (!readError && existing)
        return existing as unknown as MarketCommentAttachment;
    }
    throw error;
  }
  return data as unknown as MarketCommentAttachment;
}

export async function deleteMarketCommentAttachment(row: {
  id: number;
  bucket: string;
  path: string;
}): Promise<boolean> {
  // Best-effort storage delete
  await supabase.storage.from(row.bucket).remove([row.path]);
  const { error } = await supabase
    .from('market_comment_attachments')
    .update({ deleted_at: new Date().toISOString() })
    .eq('id', row.id);
  if (error) throw error;
  return true;
}

export async function getMarketCommentAttachmentSignedUrl(
  bucket: string,
  path: string,
  expiresInSeconds: number = 60
): Promise<string> {
  const { data, error } = await supabase.storage
    .from(bucket)
    .createSignedUrl(path, expiresInSeconds);
  if (error) throw error;
  return data.signedUrl;
}

export async function updateMarketCommentAttachment(
  id: number,
  patch: Partial<Pick<MarketCommentAttachment, 'comment' | 'name'>>
): Promise<MarketCommentAttachment> {
  const { data, error } = await supabase
    .from('market_comment_attachments')
    .update(patch)
    .eq('id', id)
    .select('*')
    .single();
  if (error) throw error;
  return data as unknown as MarketCommentAttachment;
}

// ---------------------------------------------------------------------------
// Bitácora Prices API - Get comments with prices for chart overlay
// ---------------------------------------------------------------------------

export interface BitacoraPriceData {
  id: number;
  date_from: string;
  date_to: string | null;
  variety: string | null;
  caliber: string | null;
  unit_price: number;
  unit_weight: number;
  unit_count: number; // Cantidad de bultos - for weighted average
  local_currency: string | null;
  fleet: 'A' | 'M' | null; // A = Aereo, M = Maritimo
  price_kg: number; // Calculated: unit_price / unit_weight
  // Para separar por cliente y para el hover del punto.
  seller: string | null; // Cliente (receiver del contenedor)
  label: string | null; // Etiqueta de la caja, para separar por etiqueta
  content: string | null;
  container_number: string | null;
  author: string | null;
  photo_url: string | null; // Primera foto, URL firmada
  days_from_eta: number | null; // Días entre la ETA del contenedor y la venta
}

// Las fotos viven en un bucket privado; la URL firmada dura lo suficiente
// para una sesión de trabajo mirando el gráfico.
const BITACORA_PHOTO_URL_TTL_SECONDS = 60 * 60;

export interface ListBitacoraPricesParams {
  date?: string; // YYYY-MM-DD single date
  from?: string; // YYYY-MM-DD range start
  to?: string; // YYYY-MM-DD range end
  species_id?: number;
  season?: string;
  market_id?: string;
}

const CHART_CURRENCIES = ['RMB', 'USD'];
const CHINA_COUNTRY_CODES = ['CN', 'HK'];

export async function listBitacoraPrices(
  params: ListBitacoraPricesParams
): Promise<BitacoraPriceData[]> {
  let query = supabase
    .from('market_comments')
    .select(
      `id, date_from, date_to, variety, caliber, unit_price, unit_weight,
      unit_count, local_currency, fleet, seller, label, content, container_number,
      market:markets(country_code),
      created_by:users!market_comments_created_by_fkey(first_name, last_name),
      attachments:market_comment_attachments(bucket, path, is_image, deleted_at)`
    )
    .is('deleted_at', null)
    .not('unit_price', 'is', null)
    .not('unit_weight', 'is', null)
    .gt('unit_weight', 0);

  const { date, from, to, species_id, season, market_id } = params;

  // Date logic - same as listMarketComments
  if (date) {
    query = query
      .lte('date_from', date)
      .or(`date_to.gte.${date},and(date_to.is.null,date_from.eq.${date})`);
  } else if (from && to) {
    query = query
      .lte('date_from', to)
      .or(`date_to.gte.${from},and(date_to.is.null,date_from.gte.${from})`);
  }

  if (species_id !== undefined) query = query.eq('species_id', species_id);
  if (season) query = query.eq('season', season);
  if (market_id) query = query.eq('market_id', market_id);

  const { data, error } = await query;
  if (error) throw error;

  // Los gráficos de Mercado son de China, en RMB o USD: los precios de otros
  // mercados u otras monedas quedan solo en el Consolidado.
  const rows = ((data as any[]) || []).filter(
    (row) =>
      (!row.local_currency || CHART_CURRENCIES.includes(row.local_currency)) &&
      (!row.market || CHINA_COUNTRY_CODES.includes(row.market.country_code))
  );

  // Primera foto vigente de cada registro, firmada en un solo pedido por bucket.
  const firstPhoto = new Map<number, { bucket: string; path: string }>();
  for (const row of rows) {
    const attachments: {
      bucket: string;
      path: string | null;
      is_image: boolean | null;
      deleted_at: string | null;
    }[] = row.attachments || [];
    const photo = attachments.find(
      (a) => a.is_image && !a.deleted_at && a.path
    );
    if (photo?.path) firstPhoto.set(row.id, { bucket: photo.bucket, path: photo.path });
  }
  const signedUrls = new Map<string, string>();
  const byBucket = new Map<string, string[]>();
  for (const { bucket, path } of firstPhoto.values()) {
    byBucket.set(bucket, [...(byBucket.get(bucket) || []), path]);
  }
  for (const [bucket, paths] of byBucket) {
    const { data: signed } = await supabase.storage
      .from(bucket)
      .createSignedUrls(paths, BITACORA_PHOTO_URL_TTL_SECONDS);
    for (const item of signed || []) {
      if (item.path && item.signedUrl) signedUrls.set(item.path, item.signedUrl);
    }
  }

  // ETA de los contenedores, para saber cuánto se demoró la venta.
  const containers = Array.from(
    new Set(rows.map((r) => r.container_number).filter(Boolean))
  ) as string[];
  const etaByContainer = new Map<string, string>();
  if (containers.length > 0) {
    const { data: shipments } = await supabase
      .from('shipments')
      .select('container_number, ship_eta')
      .in('container_number', containers)
      .not('ship_eta', 'is', null);
    for (const s of shipments || []) {
      if (s.container_number && s.ship_eta) {
        etaByContainer.set(s.container_number, String(s.ship_eta).slice(0, 10));
      }
    }
  }
  const daysFromEta = (container: string | null, sale: string) => {
    const eta = container ? etaByContainer.get(container) : undefined;
    if (!eta) return null;
    return Math.round(
      (new Date(`${sale}T12:00:00Z`).getTime() - new Date(`${eta}T12:00:00Z`).getTime()) /
        86_400_000
    );
  };

  // Transform to include calculated price_kg
  return rows.map((row) => ({
    id: row.id,
    date_from: row.date_from,
    date_to: row.date_to,
    variety: row.variety,
    caliber: row.caliber,
    unit_price: row.unit_price,
    unit_weight: row.unit_weight,
    unit_count: row.unit_count || 1, // Default to 1 if not set
    local_currency: row.local_currency,
    fleet: row.fleet || null,
    price_kg: row.unit_price / row.unit_weight,
    seller: row.seller || null,
    label: row.label || null,
    content: row.content || null,
    container_number: row.container_number || null,
    author:
      [row.created_by?.first_name, row.created_by?.last_name]
        .filter(Boolean)
        .join(' ') || null,
    photo_url: firstPhoto.has(row.id)
      ? signedUrls.get(firstPhoto.get(row.id)!.path) ?? null
      : null,
    days_from_eta: daysFromEta(row.container_number || null, row.date_from),
  }));
}
